import { Bot, Box, FolderGit2, MessageSquare, UserCircle, Workflow } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ResourceGrant } from '@reflex/ui/client/api/resource-grants';

/**
 * How the sharing surface presents one `resourceType`. A grant's
 * `resourceType` is a free-form string on the wire (the grant table is
 * polymorphic), so this registry is a presentation layer only: it decides
 * which section a share lands in, what that section is called, and which
 * resource-preview `kind` renders the resource chip. Types with no entry
 * still get a section, titled from the raw type.
 */
export interface ShareResourceKind {
  /** The grant's `resourceType` on the wire. */
  type: string;
  /** Section heading, plural: "Agent sessions". */
  title: string;
  /** Singular noun used in row-level copy: "session", "persona". */
  noun: string;
  /** Icon for the section header. */
  icon: LucideIcon;
  /**
   * Resource-preview `kind` for the chip (see `web/src/lib/resource-previews.ts`
   * and plugin `web.resourcePreviews`). Omitted when no preview is
   * registered: the share then falls back to a copyable id.
   */
  previewKind?: string;
}

/** The `resourceType` every share carries today. */
export const AGENT_SHARE_TYPE = 'agent';

/**
 * Known share types in the order their sections appear. Adding a resource
 * type to sharing means adding an entry here; nothing else on the page is
 * type-aware.
 */
const SHARE_RESOURCE_KINDS: ShareResourceKind[] = [
  {
    type: AGENT_SHARE_TYPE,
    title: 'Agent sessions',
    noun: 'session',
    icon: Bot,
    previewKind: 'agents',
  },
  {
    type: 'persona',
    title: 'Personas',
    noun: 'persona',
    icon: UserCircle,
    previewKind: 'personas',
  },
  { type: 'chat', title: 'Chats', noun: 'chat', icon: MessageSquare },
  { type: 'flow', title: 'Automations', noun: 'automation', icon: Workflow, previewKind: 'flows' },
  {
    type: 'project',
    title: 'Projects',
    noun: 'project',
    icon: FolderGit2,
    previewKind: 'projects',
  },
];

const KIND_BY_TYPE = new Map(SHARE_RESOURCE_KINDS.map((kind) => [kind.type, kind]));

/** Section position of a registered type; unknown types sort after all of them. */
const SECTION_ORDER = new Map(SHARE_RESOURCE_KINDS.map((kind, index) => [kind.type, index]));

/** "flow-run" -> "Flow run", for a type this build does not know about. */
function humanize(type: string): string {
  const words = type.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The presentation for a `resourceType`. Unknown types (a newer server, a
 * plugin this build does not ship) get a readable section of their own
 * rather than being dropped or lumped into someone else's.
 */
export function shareResourceKind(type: string): ShareResourceKind {
  return (
    KIND_BY_TYPE.get(type) ?? {
      type,
      title: humanize(type),
      noun: 'resource',
      icon: Box,
    }
  );
}

/** Test-only: the registered types, in section order. */
export function _listShareResourceTypes(): string[] {
  return SHARE_RESOURCE_KINDS.map((kind) => kind.type);
}

/** One resource-type section of the sharing page. */
export interface ShareSection {
  kind: ShareResourceKind;
  /** Shares addressed to the caller that they cannot manage. */
  sharedWithYou: ResourceGrant[];
  /** Manageable shares, one group per shared resource. */
  managedGroups: ResourceGrant[][];
}

/**
 * Split a flat grant list into one section per resource type, each already
 * sorted with `compare` (subject order within a resource). Known types come
 * first in registry order, unknown types after them alphabetically, and a
 * type with no shares at all gets no section.
 *
 * `canManage` is the server's own answer to "may the caller change or revoke
 * this share", so it is the split between the two halves of a section.
 */
export function groupSharesByKind(
  grants: ResourceGrant[],
  compare: (a: ResourceGrant, b: ResourceGrant) => number,
): ShareSection[] {
  const byType = new Map<string, ResourceGrant[]>();
  for (const grant of grants) {
    const bucket = byType.get(grant.resourceType);
    if (bucket) bucket.push(grant);
    else byType.set(grant.resourceType, [grant]);
  }

  const orderOf = (type: string): number => SECTION_ORDER.get(type) ?? SECTION_ORDER.size;
  const types = [...byType.keys()].sort((a, b) => orderOf(a) - orderOf(b) || a.localeCompare(b));

  return types.map((type) => {
    const grantsOfType = byType.get(type) ?? [];
    const byResource = new Map<string, ResourceGrant[]>();
    for (const grant of grantsOfType) {
      if (grant.canManage !== true) continue;
      const group = byResource.get(grant.resourceId);
      if (group) group.push(grant);
      else byResource.set(grant.resourceId, [grant]);
    }
    return {
      kind: shareResourceKind(type),
      sharedWithYou: grantsOfType.filter((grant) => grant.canManage !== true).sort(compare),
      managedGroups: [...byResource.values()].map((group) => group.sort(compare)),
    };
  });
}
